import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import {
  createDocumentList,
  updateDocumentList,
  deleteDocumentList,
  buildRegisterDocNumber,
  getRegisterDocSource,
  resolveDocumentUid,
  resolveDocumentNameAbbreviation,
  resolveDocumentNameDescription,
  ensureNumberedDocumentUid,
  documentAbbreviations,
} from "./docListAPI";
import { secondAxios } from "../../../../../../../api/axios";
import EditableTable from "../../../../../../../components/Tables/EditableTable";
import { useDCS } from "../DCSContext";
import { fetchPlantProjectChoices } from "../../UUIDRegister/uuidRegisterApi";

const abbreviationByCode = new Map(
  documentAbbreviations.map((item) => [
    String(item.abbreviation ?? "").trim(),
    item,
  ]),
);

function findDocumentNameOption(abbrOrUid) {
  const code = resolveDocumentNameAbbreviation(abbrOrUid);
  if (!code) return null;
  const known = abbreviationByCode.get(code);
  if (known) return known;
  return {
    sNo: `legacy-${code}`,
    description: `${code} (current)`,
    abbreviation: code,
  };
}

function buildDocumentNameOptions(currentAbbr) {
  const option = findDocumentNameOption(currentAbbr);
  if (option && !abbreviationByCode.has(currentAbbr)) {
    return [option, ...documentAbbreviations];
  }
  return documentAbbreviations;
}

function notifyDuidListeners(row, duid) {
  if (!Array.isArray(row._duidListeners)) return;
  row._duidListeners.forEach((fn) => {
    try {
      fn(duid);
    } catch {
      /* ignore */
    }
  });
}

function createDocumentIdentifierEdit(existingDocuments) {
  return function DocumentIdentifierEdit({ cell, column, row }) {
    const readStored = () =>
      String(
        row._valuesCache?.documentName ??
          row._valuesCache?.[column.id] ??
          row.original?.documentName ??
          row.original?.uid ??
          row.original?.department ??
          cell.getValue() ??
          "",
      ).trim();

    const [selected, setSelected] = React.useState(() =>
      findDocumentNameOption(readStored()),
    );

    React.useEffect(() => {
      if (!row._valuesCache) {
        row._valuesCache = {};
      }

      const abbr = selected?.abbreviation ?? "";
      if (!abbr) {
        row._valuesCache[column.id] = "";
        row._valuesCache.documentName = "";
        row._valuesCache.uid = "";
        notifyDuidListeners(row, "");
        return;
      }

      const ensured = ensureNumberedDocumentUid(
        {
          documentName: abbr,
          uid: row.original?.uid,
        },
        existingDocuments,
        { excludeId: row.original?.id },
      );
      const duid = ensured.documentName || "";
      // Keep column value as description for display; persist numbered DUID
      row._valuesCache[column.id] = selected?.description ?? abbr;
      row._valuesCache.documentName = duid;
      row._valuesCache.uid = duid;
      notifyDuidListeners(row, duid);
    }, [selected, column.id, row]);

    const options = buildDocumentNameOptions(
      resolveDocumentNameAbbreviation(readStored()),
    );

    return (
      <Autocomplete
        size="small"
        fullWidth
        options={options}
        value={selected}
        onChange={(_event, option) => setSelected(option)}
        getOptionLabel={(option) => option?.description ?? ""}
        isOptionEqualToValue={(a, b) =>
          String(a?.abbreviation ?? "") === String(b?.abbreviation ?? "")
        }
        filterOptions={(opts, { inputValue }) => {
          const q = inputValue.trim().toUpperCase();
          if (!q) return opts;
          return opts.filter(
            (o) =>
              String(o.description ?? "")
                .toUpperCase()
                .includes(q) ||
              String(o.abbreviation ?? "")
                .toUpperCase()
                .includes(q),
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select document type…"
            variant="standard"
          />
        )}
        sx={{ minWidth: 280, width: "100%" }}
      />
    );
  };
}

function DuidReadonlyEdit({ column, row }) {
  const [duid, setDuid] = React.useState(() =>
    resolveDocumentUid({
      documentName:
        row._valuesCache?.documentName ?? row.original?.documentName,
      uid: row._valuesCache?.uid ?? row.original?.uid,
    }),
  );

  React.useEffect(() => {
    if (!row._duidListeners) row._duidListeners = [];
    const listener = (next) => setDuid(next || "");
    row._duidListeners.push(listener);
    setDuid(
      resolveDocumentUid({
        documentName:
          row._valuesCache?.documentName ?? row.original?.documentName,
        uid: row._valuesCache?.uid ?? row.original?.uid,
      }),
    );
    return () => {
      row._duidListeners = (row._duidListeners || []).filter(
        (fn) => fn !== listener,
      );
    };
  }, [row]);

  React.useEffect(() => {
    if (!row._valuesCache) row._valuesCache = {};
    row._valuesCache.uid = duid;
    row._valuesCache[column.id] = duid;
    if (duid) row._valuesCache.documentName = duid;
  }, [duid, column.id, row]);

  return (
    <TextField
      size="small"
      fullWidth
      variant="standard"
      value={duid}
      placeholder="—"
      InputProps={{ readOnly: true }}
    />
  );
}

export const DocLsitInitialData = {
  companyName: "",
  projectNumber: "",
  documentName: "",
  uid: "",
  revision: "",
  numberOfSheets: "",
  documentNumber: "",
  description: "",
  projectId: "",
};

const headerCenterSx = {
  "& .Mui-TableHeadCell-Content": {
    justifyContent: "center",
  },
  textAlign: "center",
  fontWeight: 700,
};

export const DocListColumns = (
  _allUsers,
  _type,
  plantName = "",
  existingDocuments = [],
) => {
  const fixedPlantName = String(plantName ?? "").trim();
  const DocumentIdentifierEdit =
    createDocumentIdentifierEdit(existingDocuments);

  return [
    {
      accessorKey: "projectNumber",
      header: "PUID",
      size: 20,
      enableEditing: false,
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
      accessorFn: (row) =>
        fixedPlantName || String(row.projectNumber ?? "").trim(),
      Cell: ({ row }) => {
        const v =
          fixedPlantName ||
          String(row.original?.projectNumber ?? row.projectNumber ?? "").trim();
        return v !== "" ? v : "—";
      },
    },
    {
      id: "documentIdentifier",
      header: "Document Identifier",
      size: 36,
      enableEditing: true,
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
      accessorFn: (row) =>
        resolveDocumentNameDescription(
          row.documentName ?? row.uid ?? row.department ?? "",
        ),
      Edit: DocumentIdentifierEdit,
      Cell: ({ row }) => {
        const label = resolveDocumentNameDescription(
          row.original?.documentName ??
            row.original?.uid ??
            row.original?.department ??
            row.documentName ??
            "",
        );
        return label !== "" ? label : "—";
      },
    },
    {
      accessorKey: "uid",
      header: "Abbreviation",
      size: 22,
      enableEditing: true,
      Edit: DuidReadonlyEdit,
      muiTableHeadCellProps: {
        sx: {
          ...headerCenterSx,
          backgroundColor: "#FFE0B2",
        },
      },
      accessorFn: (row) =>
        resolveDocumentUid(row) ||
        String(row.documentName ?? row.uid ?? "").trim(),
      Cell: ({ row }) => {
        const uid = resolveDocumentUid(
          row?.original ? getRegisterDocSource(row) : row,
        );
        return uid !== "" ? uid : "—";
      },
    },
    {
      id: "documentNumber",
      header: "DUID",
      size: 44,
      enableEditing: false,
      muiTableHeadCellProps: {
        sx: {
          ...headerCenterSx,
          backgroundColor: "#C8E6C9",
        },
      },
      accessorFn: (row) =>
        buildRegisterDocNumber({
          projectNumber:
            fixedPlantName || String(row.projectNumber ?? "").trim(),
          documentName: row.documentName,
          uid: row.uid,
        }),
      Cell: ({ row }) => {
        const source = getRegisterDocSource(row);
        const resolved = buildRegisterDocNumber({
          ...source,
          projectNumber:
            fixedPlantName ||
            String(source.projectNumber ?? "").trim(),
        });
        return resolved !== "" ? resolved : "—";
      },
    },
    {
      accessorKey: "revision",
      header: "Latest Revision",
      size: 15,
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
    },
    {
      accessorKey: "numberOfSheets",
      header: "No of Sheets",
      size: 24,
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
    },
    {
      id: "description",
      accessorFn: (row) => row.description ?? row.title ?? "",
      header: "Description",
      size: 36,
      muiTableHeadCellProps: {
        sx: {
          ...headerCenterSx,
          backgroundColor: "#BBDEFB",
        },
      },
    },
  ];
};

const DocumentList = ({ open, handleClose }) => {
  const {
    allUsers,
    type,
    activeProjectId,
    plant,
    plantId,
    documentList,
    getDocList,
    documentListLoaded,
  } = useDCS();

  const [uuidProjectChoices, setUuidProjectChoices] = useState([]);
  const [plantName, setPlantName] = useState("");

  const documentListRef = useRef(documentList);
  documentListRef.current = documentList;
  const documentListLoadedRef = useRef(documentListLoaded);
  documentListLoadedRef.current = documentListLoaded;

  const defaultPlantProjectIdRef = useRef("");
  const plantNameRef = useRef("");
  const needsNetworkFetchRef = useRef(false);

  const filterDocumentsByType = useCallback(
    (list) => {
      if (!type || type === "all") return list || [];
      return (list || []).filter((doc) => doc.type === type);
    },
    [type],
  );

  const existingDocuments = useMemo(
    () => filterDocumentsByType(documentList || []),
    [documentList, filterDocumentsByType],
  );

  useEffect(() => {
    if (!open || !plant || !plantId) {
      setPlantName("");
      return;
    }
    let cancelled = false;
    secondAxios
      .get(`/plant/${plantId}`)
      .then((res) => {
        if (!cancelled) setPlantName(res.data?.name || "");
      })
      .catch(() => {
        if (!cancelled) setPlantName("");
      });
    return () => {
      cancelled = true;
    };
  }, [open, plant, plantId]);

  useEffect(() => {
    if (!open || !plant || !plantId) {
      setUuidProjectChoices([]);
      return undefined;
    }

    let cancelled = false;

    const loadProjects = async () => {
      try {
        const choices = await fetchPlantProjectChoices(plantId);
        if (!cancelled) setUuidProjectChoices(choices);
      } catch (e) {
        console.warn("Document Register project choices load failed:", e);
        if (!cancelled) setUuidProjectChoices([]);
      }
    };

    loadProjects();
    return () => {
      cancelled = true;
    };
  }, [open, plant, plantId]);

  const defaultPlantProjectId =
    uuidProjectChoices.length > 0 ? uuidProjectChoices[0].id : "";

  defaultPlantProjectIdRef.current = defaultPlantProjectId;
  plantNameRef.current = plantName;

  const columns = useMemo(
    () =>
      DocListColumns(allUsers, type, plantName, existingDocuments),
    [allUsers, type, plantName, existingDocuments],
  );

  const parentId = plant && plantId ? plantId : activeProjectId;

  const getItems = useCallback(async () => {
    if (
      !needsNetworkFetchRef.current &&
      documentListLoadedRef.current &&
      documentListRef.current != null
    ) {
      return filterDocumentsByType(documentListRef.current);
    }

    needsNetworkFetchRef.current = false;
    const list = await getDocList();
    return filterDocumentsByType(list ?? documentListRef.current ?? []);
  }, [filterDocumentsByType, getDocList]);

  const withFixedProjectRef = useCallback((item) => {
    const name = String(plantNameRef.current ?? "").trim();
    if (!name) return item;
    return { ...item, projectNumber: name };
  }, []);

  const handleCreateItem = useCallback(
    async (createParentId, item) => {
      needsNetworkFetchRef.current = true;

      const existing = filterDocumentsByType(documentListRef.current || []);
      const withUid = withFixedProjectRef(
        ensureNumberedDocumentUid(item, existing),
      );

      if (plant && plantId) {
        const selectedProjectId =
          String(withUid.projectId ?? "").trim() ||
          defaultPlantProjectIdRef.current;
        if (!selectedProjectId) {
          throw new Error(
            "No project available for this document register row.",
          );
        }
        const { projectId, projectName, companyName, ...docData } = withUid;
        return createDocumentList(type, selectedProjectId, docData);
      }
      return createDocumentList(type, createParentId, withUid);
    },
    [plant, plantId, type, filterDocumentsByType, withFixedProjectRef],
  );

  const handleUpdateItem = useCallback(
    async (updateDocument) => {
      needsNetworkFetchRef.current = true;
      const existing = filterDocumentsByType(documentListRef.current || []);
      const withUid = withFixedProjectRef(
        ensureNumberedDocumentUid(updateDocument, existing, {
          excludeId: updateDocument.id,
        }),
      );
      return updateDocumentList(withUid);
    },
    [filterDocumentsByType, withFixedProjectRef],
  );

  const handleDeleteItem = useCallback(async (id) => {
    needsNetworkFetchRef.current = true;
    return deleteDocumentList(id);
  }, []);

  const tableApi = useMemo(
    () => ({
      getItems,
      createItem: handleCreateItem,
      updateItem: handleUpdateItem,
      deleteItem: handleDeleteItem,
    }),
    [getItems, handleCreateItem, handleUpdateItem, handleDeleteItem],
  );

  const initialData = useMemo(
    () => ({
      ...DocLsitInitialData,
      projectNumber: plantName || "",
      ...(defaultPlantProjectId ? { projectId: defaultPlantProjectId } : {}),
    }),
    [defaultPlantProjectId, plantName],
  );

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "96vw", md: "min(1280px, 96vw)" },
          maxHeight: "90vh",
          overflow: "auto",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Typography variant="h6" component="h2" gutterBottom>
          Document Register
        </Typography>
        {plant && plantName ? (
          <Typography variant="h4" color="darkBlue" sx={{ pb: 2 }}>
            {plantName}
          </Typography>
        ) : null}
        {open ? (
          <EditableTable
            name="Doc Register"
            displayMode="row"
            columns={columns}
            enableEditing={true}
            enableCreate={true}
            initialData={initialData}
            api={tableApi}
            parentId={parentId}
          />
        ) : null}
      </Box>
    </Modal>
  );
};

export default DocumentList;
